import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonLevel, ButtonType } from '@challenger6/vm-pattern-library/button';
import { EMPTY, Subscription, iif, of } from 'rxjs';

import { finalize, switchMap } from 'rxjs/operators';

import { NotificationType } from '@challenger6/vm-pattern-library/notification';
import {
  IdentifyCustomerByAddressResponse,
  IdentifyCustomerByAddressService,
} from '../../../billing/services/identify-customer-by-address.service';
import { LatLongToPostcodeService } from '../../../billing/services/lat-long-to-postcode.service';
import { PostcodeCheckerContent } from '../../models/postcode-checker-content.model';

@Component({
  selector: 'vm-postcode-checker-section',
  templateUrl: './postcode-checker-section.component.html',
  styleUrls: ['./postcode-checker-section.component.scss'],
})
export class PostcodeCheckerSectionComponent implements OnInit, OnDestroy {
  @Input() data: PostcodeCheckerContent;
  @Output() setAPILoadingState = new EventEmitter<boolean>();

  public form: FormGroup;
  buttonType = ButtonType;
  buttonLevel = ButtonLevel;
  showHouseNumber: boolean;
  showError: boolean;
  notificationMessage: string;
  findByDetailsSubscription: Subscription;
  nextSubscription: Subscription;
  type = NotificationType.WARNING;
  navigatorObj: any = navigator;
  postCodeSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private identifyCustomerByAddressService: IdentifyCustomerByAddressService,
    private latLongToPostcodeService: LatLongToPostcodeService
  ) {}

  ngOnInit(): void {
    this.setupForm();
  }

  setupForm(): void {
    this.form = this.formBuilder.group({
      postcode: ['', Validators.required],
      lastName: ['', [Validators.required, Validators.pattern("[a-zA-Z ,.'-]*")]],
      house: [''],
    });
  }

  submitForm() {
    this.setAPILoadingState.emit(true);
    this.form.markAllAsTouched();
    if (this.form?.valid) {
      this.identifyCustomerByAddressService
        .findByDetails(this.form.value)
        .pipe(
          switchMap((findByDetailsResponse: any) => {
            return iif(() => !findByDetailsResponse?.error, this.checkCustomer(findByDetailsResponse), EMPTY);
          }),
          finalize(() => {
            this.setAPILoadingState.emit(false);
          })
        )
        .subscribe((response: never | any) => {          
          if (response?.submitLink) {
            window.location.href = response?.submitLink;
          }
        });
    }
  }

  checkCustomer(findByDetailsResponse) {
    if (findByDetailsResponse === IdentifyCustomerByAddressResponse.MULTIPLE_RESULTS) {
      this.form.get('house')?.markAsUntouched();
      this.showError = this.showHouseNumber;
      this.showHouseNumber = true;
      return EMPTY;
    } else if (findByDetailsResponse === IdentifyCustomerByAddressResponse.NO_RESULT) {
      this.showError = true;
      return EMPTY;
    } else {
      return of({ submitLink: this.data?.submitLink });
    }
  }

  getPostCode() {
    if (this.navigatorObj?.geolocation) {
      this.navigatorObj.geolocation.getCurrentPosition((geo) => {
        const position = {
          lat: geo?.coords?.latitude,
          lon: geo?.coords?.longitude,
        };
        this.postCodeSubscription = this.latLongToPostcodeService
          .getPostcodeByLocation(position)
          .subscribe((res: any) => {
            if (res?.result?.length === 0) {
              return;
            }
            const address = res.result[0];
            this.form.patchValue({
              postcode: address.postcode,
            });
          });
      });
    }
  }

  ngOnDestroy() {
    this.findByDetailsSubscription?.unsubscribe();
    this.nextSubscription?.unsubscribe();
    this.postCodeSubscription?.unsubscribe();
  }
}
